import { useCallback, useMemo } from "react";

const STORAGE_KEY = "np_category_reads";

interface CategoryReads {
  [category: string]: number;
}

function getReads(): CategoryReads {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Record that the user read an article in a given category */
export function trackCategoryRead(category: string) {
  try {
    const reads = getReads();
    reads[category] = (reads[category] ?? 0) + 1;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reads));
  } catch {
    // localStorage unavailable
  }
}

/**
 * Returns a comparator that sorts categories by read count (descending).
 * Categories the user has never read keep their original order.
 */
export function useCategoryInterest() {
  const reads = useMemo(() => getReads(), []);

  const sortCategories = useCallback(
    <T extends string>(categories: T[]): T[] => {
      const hasAny = Object.values(reads).some((v) => v > 0);
      if (!hasAny) return categories;

      return [...categories].sort((a, b) => {
        const aCount = reads[a] ?? 0;
        const bCount = reads[b] ?? 0;
        // Both unread: keep original order
        if (aCount === 0 && bCount === 0) return 0;
        return bCount - aCount;
      });
    },
    [reads],
  );

  /** Sort an array of objects that have a category key */
  const sortByCategory = useCallback(
    <T extends { category: string }>(items: T[]): T[] => {
      const hasAny = Object.values(reads).some((v) => v > 0);
      if (!hasAny) return items;

      return [...items].sort((a, b) => {
        const aCount = reads[a.category] ?? 0;
        const bCount = reads[b.category] ?? 0;
        if (aCount === 0 && bCount === 0) return 0;
        return bCount - aCount;
      });
    },
    [reads],
  );

  return { reads, sortCategories, sortByCategory };
}
